<script setup lang="ts">
import { computed, onMounted, ref, watch, watchEffect } from "vue";

import { useI18n } from "vue-i18n";
import { formatMetadata } from "@/common/utils/common";
import { toast } from "@/common/utils/toast";

// import { historyAnswerAdd } from "@/common/api/rest/history";

import {
  baseCamera,
  baseCheckbox,
  baseConfirm,
  baseInput,
  baseRadio,
  baseSelect,
  baseTextarea,
  baseTooltip,
  svgIcon,
} from "@/common/components";

import { fileAdd, fileGet, fileDel } from "@/common/api/rest/files";

import type { AnswersType, QuestionType } from "@/common/types/question";

interface PayloadType {
  [key: string]: any;
}

const { t } = useI18n();

const emit = defineEmits(["update:data", "del"]);

const props = defineProps<{
  accountId: string;
  ownerId: string;
  answersId: string;
  answers: AnswersType | null;
  questions: QuestionType[] | null;
  field: string;
  code: string;
  name: string;
  orders: string[];
}>();

const payload = ref<PayloadType>({});

const selectedIndex = ref<number | null>(null);
const images = ref<{ [key: string]: string }>({});

const showCamera = ref(false);
const showConfirmImageDel = ref(false);

function formatAnswers() {
  if (!props.questions || !props.answers?.metadata) return;

  try {
    const metadata = props.answers.metadata;

    if (metadata?.[props.field]?.[props.ownerId]) {
      Object.entries(metadata[props.field][props.ownerId])?.forEach(
        ([key, value]) => {
          if (value?.file_id) {
            if (value?.type === "image") {
              fileGet(value.file_id)
                .then((response) => {
                  const { success, data } = response;
                  console.log("data", data);
                  if (success) {
                    images.value[key] = `${data.infos},${data.base64}`;
                  }
                })
                .catch(() => {
                  toast({ message: t("error.default"), type: "error" });
                });
            }
          }
          payload.value[key] = value;
        },
      );
    }
  } catch (error) {
    console.log("error", error);
  }
}

function formatQuestions() {
  if (!props.questions) return;

  payload.value = (props.questions || [])
    ?.sort((a, b) => {
      if (!props.orders) return 0;

      const indexA = props.orders?.indexOf(a.question_id);
      const indexB = props.orders?.indexOf(b.question_id);

      return indexA - indexB;
    })
    ?.reduce((acc, item) => {
      if (item.type === "title") return acc;

      acc = {
        ...acc,
        [item.question_id]: null,
      };
      return acc;
    }, {});
}

function update() {
  const metadata = props.answers?.metadata ?? {};

  const formatted = {
    ...metadata,
    [props.field]: {
      ...metadata?.[props.field],
      [props.ownerId]: payload.value,
    },
  };

  emit("update:data", formatted);
}

// async function updateSingle(questionId: string) {
//   const fieldName = `metadata.${props.field}.${props.ownerId}.${questionId}`;
//   const value = payload.value[questionId];

//   await historyAnswerAdd(props.accountId, props.answersId, {
//     field_name: fieldName,
//     new_value: value,
//   });
// }

// async function updateMultiple(questionId: string) {
//   const fieldName = `metadata.${props.field}.${props.ownerId}.${questionId}`;
//   const value = JSON.stringify(payload.value[questionId]);

//   await historyAnswerAdd(props.accountId, props.answersId, {
//     field_name: fieldName,
//     new_value: value,
//   });
// }

function openCamera(index: number) {
  selectedIndex.value = index;
  showCamera.value = true;
}

function closeCamera() {
  selectedIndex.value = null;
  showCamera.value = false;
}

function uploadImage(image: string) {
  if (selectedIndex.value === null || !props.questions || !payload.value)
    return;

  const question = props.questions[selectedIndex.value];
  if (!question) return;

  showCamera.value = false;
  const fileId = `${props.ownerId}_${question.question_id}`;

  fileAdd(fileId, {
    name: question.name,
    infos: "data:image/png;base64",
    file: image.replace("data:image/png;base64,", ""),
    extension: "png",
    size: 100,
    type: "image",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        payload.value[question.question_id] = data[0];
        update();
        // updateMultiple(question.question_id);

        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function delImage(index: number) {
  selectedIndex.value = index;
  showConfirmImageDel.value = true;
}

function delImageCancel() {
  selectedIndex.value = null;
  showConfirmImageDel.value = false;
}

function delImageConfirm() {
  if (selectedIndex.value === null || !props.questions || !payload.value)
    return;

  const question = props.questions[selectedIndex.value];
  if (!question) return;

  showConfirmImageDel.value = false;

  fileDel(payload.value[question.question_id].file_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        payload.value[question.question_id] = null;
        delImageCancel();
        update();
        // updateMultiple(question.question_id);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => props.answers,
  () => {
    formatAnswers();
  },
);

watch(
  () => props.questions,
  () => {
    formatQuestions();
    formatAnswers();
  },
);

onMounted(() => {
  formatQuestions();
  formatAnswers();
});
</script>

<template>
  <section class="view-form">
    <div class="header">
      <div class="infos">
        <div class="title">
          {{ props.code }}
        </div>
        <div class="subtitle">
          {{ props.name }}
        </div>
      </div>
    </div>

    <div v-if="payload" class="questions">
      <template v-for="(item, index) in questions" :key="index">
        <template v-if="item.type === 'TITLE'">
          <div class="title">
            <div class="name">
              {{ item.name }}
            </div>
            <div class="description">
              {{ item.description }}
            </div>
          </div>
        </template>
        <template v-else>
          <template v-if="item.type === 'INPUT'">
            <div class="question">
              <base-input
                v-model="payload[item.question_id]"
                :value="payload[item.question_id]"
                :required="item.required"
                :placeholder="item.placeholder"
                @blur="update()"
              >
                <template #label>
                  {{ item.name }} {{ item.required ? "*" : "" }}
                </template>
              </base-input>
            </div>
          </template>

          <template v-if="item.type === 'TEXTAREA'">
            <div class="question">
              <base-textarea
                v-model="payload[item.question_id]"
                :value="payload[item.question_id]"
                :required="item.required"
                :placeholder="item.placeholder"
                @blur="update()"
              >
                <template #label>
                  {{ item.name }} {{ item.required ? "*" : "" }}
                </template>
              </base-textarea>
            </div>
          </template>

          <template v-else-if="item.type === 'RADIO'">
            <div class="question">
              <base-radio
                v-model="payload[item.question_id]"
                :id="item.question_id"
                :value="payload[item.question_id]?.toString()"
                :options="item.metadata?.options || []"
                :required="item.required"
                @change="update()"
              >
                <template #label>
                  {{ item.name }} {{ item.required ? "*" : "" }}
                </template>
              </base-radio>
            </div>
          </template>

          <template v-else-if="item.type === 'SELECT'">
            <div class="question">
              <base-select
                v-model="payload[item.question_id]"
                :id="item.question_id"
                :value="payload[item.question_id]?.toString()"
                :options="item.metadata?.options || []"
                :required="item.required"
                @change="update()"
              >
                <template #label>
                  {{ item.name }} {{ item.required ? "*" : "" }}
                </template>
              </base-select>
            </div>
          </template>

          <template v-else-if="item.type === 'CHECKBOX'">
            <div class="question">
              <base-checkbox
                v-model="payload[item.question_id]"
                :id="item.question_id"
                :options="item.metadata?.options || []"
                :value="payload[item.question_id]"
                @change="update()"
              >
                <template #label>
                  {{ item.name }} {{ item.required ? "*" : "" }}
                </template>
              </base-checkbox>
            </div>
          </template>

          <template v-else-if="item.type === 'PHOTO'">
            <div class="question">
              <div class="photo">
                <div class="header">
                  <div class="label">
                    {{ item.name }}
                  </div>
                  <template v-if="item.description">
                    <div class="description">
                      {{ item.description }}
                    </div>
                  </template>
                </div>

                <div class="content">
                  <template v-if="payload[item.question_id]?.path">
                    <img :src="images[item.question_id]" alt="" />

                    <div class="actions">
                      <button
                        type="button"
                        class="btn btn-md btn-danger px-4"
                        @click="delImage(index)"
                      >
                        {{ t("common.labels.delete") }}
                      </button>
                    </div>
                  </template>
                  <template v-else>
                    <button
                      type="button"
                      class="btn btn-md btn-default px-4"
                      @click="openCamera(index)"
                    >
                      {{ t("common.labels.capture") }}
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </template>
      </template>
    </div>

    <template v-if="showCamera">
      <base-camera @capture="uploadImage($event)" @close="closeCamera()" />
    </template>

    <base-confirm
      max-width="600px"
      :show="showConfirmImageDel"
      :title="t('common.modals.titles.remove_image').toUpperCase()"
      :message="t('common.modals.messages.remove_image')"
      @cancel="delImageCancel()"
      @confirm="delImageConfirm()"
    />
  </section>
</template>

<style lang="scss" scoped>
.view-form {
  display: flex;
  flex-direction: column;
  padding-block: 1.5rem;
  padding-block-end: 2.5rem;
  padding-inline: 1.5rem;
  gap: 1rem;

  .header {
    display: flex;
    flex-direction: row;
    gap: 1rem;

    .infos {
      flex: 3;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-block-end: 0.5rem;

      .title {
        font-size: 1.15rem;
        font-weight: bold;
      }

      .subtitle {
        font-size: 1rem;
      }
    }

    .action {
      width: auto;

      button {
        &:hover {
          color: var(--red);
        }
      }
    }
  }

  .questions {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;

    .title {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-block: 0.5rem;

      .name {
        font-size: 1rem;
        font-weight: bold;
      }

      .description {
        font-size: 0.875rem;
      }
    }
  }

  .photo {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    .header {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      .label {
        font-size: 1rem;
      }

      .description {
        font-size: 0.875rem;
      }
    }

    .content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      background: #f1f1f1;
      padding: 1.5rem;

      img {
        width: 90%;
      }

      .actions {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        padding-block-start: 1rem;
      }
    }
  }
}
</style>
